import {
	getMultipleAnalysisResult,
	getMultipleAnalysisStatus,
	getLineAnalysisStatus,
	getLineAnalysisResult,
	getPolygonAnalysisStatus,
	getPolygonAnalysisResult,
	layerStatistics
} from "api/layer";
import Options from "components/options/options";
import { RightPanel, Option } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ClimateResults from ".//climate-results/climate-results";
import AnalysisResults from "./analysis-results/analysis-results";
import ClimateMultipleResults from "./multiple-results/climate-multiple-results/climate-multiple-results";
import HazardMultipleResults from "./multiple-results/hazard-multiple-results/hazard-multiple-results";
import styles from "./results.module.scss";
import EventMultipleResults from "./multiple-results/event-multiple-results/event-multiple-results";
import LineResults from "./shape-results/line-results/line-results"
import PolygonResults from "./shape-results/polygon-results/polygon-results";
import { createActiveAnalysisJob } from "./shared/active-analysis-job";

// 模块级去重：防止组件重渲染/重复轮询导致同一 job 多次埋点
const finalizedLineResultJobs = new Set<string>();
const requestedLineResultJobs = new Set<string>();

type RequestCounterKey = "event" | "hazard" | "climateChange" | "line";

export function Results() {
	const [t] = useTranslation();
	const [context, dispatch] = useContext(AppContext);
	const [option, setOption] = useState<Option>(Option.HAZARD);
	const locationSetAnalysed = context.currentMultipleLocationsAnalysed;

	const requestNumberRef = useRef<Record<RequestCounterKey, number>>({
		event: 0,
		hazard: 0,
		climateChange: 0,
		line: 0
	});

	const getRequestCounterKey = (value: string) => value as RequestCounterKey;

	const lineResultTrackRef = useRef<
		Map<string, {
			startTime: number;
			shapeSource?: string;
			layers: string[];
			finalized: boolean;
			resultRequested: boolean;
		}>
	>(new Map());
	const activeLineJobRef = useRef(createActiveAnalysisJob());
	const activePolygonJobRef = useRef(createActiveAnalysisJob());
	const activeMultipleJobRef = useRef(createActiveAnalysisJob());

	useEffect(() => {
		if (context.lineJobId) {
			activeLineJobRef.current.activate(context.lineJobId);
		}
	}, [context.lineJobId]);

	useEffect(() => {
		if (context.polygonJobId) {
			activePolygonJobRef.current.activate(context.polygonJobId);
		}
	}, [context.polygonJobId]);

	useEffect(() => {
		if (context.jobId) {
			activeMultipleJobRef.current.activate(context.jobId);
		}
	}, [context.jobId]);

	const selectOption = (option: Option): void => {
		setOption(option);
		dispatch({ type: AppAction.setOption, payload: option });
	};

	const mergeClimateDataSimple = (resultData: any[], data: any[]) => {
		// 1) 用 Map 按 id 建索引
		const resultMap = new Map<string, any>();

		// 2) 先处理 resultData
		resultData.forEach(item => {
			// 同时为 climateRiskScores 建索引 Set
			item._riskIndexSet = new Set(
				(item.climateRiskScores || []).map((s: any) => s.index)
			);
			resultMap.set(item.id, item);
		});

		// 3) 合并 data
		data.forEach(item => {
			const existing = resultMap.get(item.id);

			if (!existing) {
				// 深拷贝并初始化 Set
				resultMap.set(item.id, {
					...item,
					climateRiskScores: [...(item.climateRiskScores || [])],
					_riskIndexSet: new Set(
						(item.climateRiskScores || []).map((s: any) => s.index)
					)
				});
			} else {
				// O(1) 判重合并
				item.climateRiskScores?.forEach((score: any) => {
					if (!existing._riskIndexSet.has(score.index)) {
						existing._riskIndexSet.add(score.index);
						existing.climateRiskScores.push(score);
					}
				});
			}
		});

		// 4) 清理临时字段并返回结果
		return Array.from(resultMap.values()).map(item => {
			delete item._riskIndexSet;
			return item;
		});
	};

	const saveAnalysisResult = (store: any, results: any) => {
		const nextStore: Map<any, any> = new Map(store || []);

		results.forEach((item: any) => {
			const normalizedItem = {
				...item,
				pointId: String(item?.pointId || "").split("-").join("")
			};
			const { layerId, pointId } = normalizedItem;

			const currentLayerMap = nextStore.get(layerId) as Map<any, any> | undefined;
			const nextLayerMap: Map<any, any> = new Map(currentLayerMap || []);

			// 规范 pointId，去除连接符后再入库
			nextLayerMap.set(pointId, normalizedItem);
			nextStore.set(layerId, nextLayerMap);
		});

		return nextStore;
	}

	const resetMultipleAnalysisData = async (tabId: string, jobId: string) => {
		if (!locationSetAnalysed) {
			return;
		}
		if (!activeMultipleJobRef.current.is(jobId)) {
			return;
		}
		let res: any = await getMultipleAnalysisStatus(jobId);
		if (!activeMultipleJobRef.current.is(jobId)) {
			return;
		}
		if (res && res.code !== 200) {
			if (context.hazardMultipleAnalysisStatus === "running") {
				dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: "error" });
			}
			if (context.climateMultipleAnalysisStatus === "running") {
				dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: "error" });
			}
			if (context.eventAnalysisStatus === "running") {
				dispatch({ type: AppAction.setEventAnalysisStatus, payload: "error" });
			}
			return;
		}
		let status = res.data.find((item: any) => item.tabId === tabId);
		if (status?.hasError) {
			if (tabId === "event") {
				dispatch({ type: AppAction.setEventAnalysisStatus, payload: "error" });
			} else if (tabId === "hazard") {
				dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: "error" });
			} else {
				dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: "error" });
			}
		} else if (status?.isCompleted) {
			requestNumberRef.current[getRequestCounterKey(tabId)] = 0;
			getMultipleAnalysisResultFn(tabId, jobId);
		} else {
			setTimeout(() => {
				if (!activeMultipleJobRef.current.is(jobId)) {
					return;
				}
				const tabKey = getRequestCounterKey(tabId);
				requestNumberRef.current[tabKey]++;
				if (requestNumberRef.current[tabKey] >= 11) {
					requestNumberRef.current[tabKey] = 0;
					if (tabId === "event") {
						dispatch({ type: AppAction.setEventAnalysisStatus, payload: "error" });
					} else if (tabId === "hazard") {
						dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: "error" });
					} else {
						dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: "error" });
					}
					return;
				}
				resetMultipleAnalysisData(tabId, jobId);
			}, 5000);
		}
	};

	const getMultipleAnalysisResultFn = async (tabId: string, jobId: string) => {
		if (!activeMultipleJobRef.current.is(jobId)) {
			return;
		}
		let tabIndex = "1";
		if (tabId === "event") {
			tabIndex = "3";
		}
		else if (tabId === "climateChange") {
			tabIndex = "4";
		}
		let res: any = await getMultipleAnalysisResult(jobId, tabIndex);
		if (!activeMultipleJobRef.current.is(jobId)) {
			return;
		}
		if (res.code !== 200 || !res.data?.results?.length) {
			if (tabId === "event") {
				dispatch({ type: AppAction.setEventAnalysisStatus, payload: "error" });
				return
			}
			if (tabId === "hazard") {
				dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: "error" });
				return;
			}
			if (tabId === "climateChange") {
				dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: "error" });
				return;
			}
			return;
		}

		if (tabId === "event") {
			dispatch({ type: AppAction.setEventAnalysisStatus, payload: undefined });
			let storeData = saveAnalysisResult(context.eventAnalysisStore || new Map(), res.data.results);
			dispatch({ type: AppAction.setEventAnalysisStore, payload: storeData });
		} else if (tabId === "hazard") {
			dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: undefined });
			let storeData = saveAnalysisResult(context.hazardMultipleAnalysisStore || new Map(), res.data.results);
			dispatch({ type: AppAction.setHazardMultipleAnalysisStore, payload: storeData });
		} else {
			let resultData: any = [];

			if (context.climateMultipleAnalysisResult && context.climateMultipleAnalysisResult.length > 0) {
				resultData = mergeClimateDataSimple(res.data.results, context.climateMultipleAnalysisResult || [])
			} else {
				resultData = res.data.results;
			}
			dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: undefined });
			dispatch({ type: AppAction.setClimateMultipleAnalysisResult, payload: resultData });
		}
	};

	const getMultipleAnalysisData = async () => {
		if (!context.jobId || !locationSetAnalysed) {
			return;
		}
		let jobId = context.jobId
		activeMultipleJobRef.current.activate(jobId);
		let res: any = await getMultipleAnalysisStatus(jobId);
		if (!activeMultipleJobRef.current.is(jobId)) {
			return;
		}
		dispatch({ type: AppAction.setJobId, payload: undefined });
		if (res && res.code !== 200 && !res.data?.[0]) {
			if (context.hazardMultipleAnalysisStatus === "running") {
				dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: "error" });
			}
			if (context.climateMultipleAnalysisStatus === "running") {
				dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: "error" });
			}
			if (context.eventAnalysisStatus === "running") {
				dispatch({ type: AppAction.setEventAnalysisStatus, payload: "error" });
			}
			return;
		}

		res.data.forEach((item: any) => {
			if (!activeMultipleJobRef.current.is(jobId)) {
				return;
			}
			if (item.hasError) {
				if (item.tabId === "event") {
					dispatch({ type: AppAction.setEventAnalysisStatus, payload: "error" });
				} else if (item.tabId === "hazard") {
					dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: "error" });
				} else {
					dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: "error" });
				}
			} else if (item.isCompleted) {
				getMultipleAnalysisResultFn(item.tabId, jobId);
			} else {
				setTimeout(() => {
					if (!activeMultipleJobRef.current.is(jobId)) {
						return;
					}
					requestNumberRef.current[getRequestCounterKey(item.tabId)] = 0;
					resetMultipleAnalysisData(item.tabId, jobId);
				}, 3000);
			}
		});
	};

	const resetLineAnalysisData = async (jobId: string) => {
		await pollShapeAnalysisStatus({
			jobId,
			isActive: (id) => activeLineJobRef.current.is(id),
			fetchStatus: getLineAnalysisStatus,
			isRunning: () => context.lineAnalysisStatus === "running",
			onError: setLineAnalysisError,
			onCompleted: getLineAnalysisResultData,
			onRetry: resetLineAnalysisData,
			onRetryOverflow: () => {
				dispatch({ type: AppAction.setLineAnalysisStatus, payload: "error" });
			}
		});
	};

	const getLineResultTrackMeta = (jobId: string) => {
		const normalizedJobId = String(jobId);
		const fromRef = lineResultTrackRef.current.get(normalizedJobId);
		if (fromRef) {
			return fromRef;
		}

		const shapeSource =
			context.currentShape?.source ||
			context.shapes.find((pl) => pl.id === context.selectedShapeId)?.source;
		const layers = ((context.rightPanel === RightPanel.Polygon ? context.polygonSelectLayer : context.lineSelectLayer)?.[0] || []) as string[];
		const fallbackMeta = {
			startTime: Date.now(),
			shapeSource,
			layers,
			finalized: finalizedLineResultJobs.has(normalizedJobId),
			resultRequested: requestedLineResultJobs.has(normalizedJobId)
		};
		lineResultTrackRef.current.set(normalizedJobId, fallbackMeta);
		return fallbackMeta;
	};

	const isLineResultFinalized = (jobId: string) => {
		const normalizedJobId = String(jobId);
		if (finalizedLineResultJobs.has(normalizedJobId)) {
			return true;
		}
		const meta = lineResultTrackRef.current.get(normalizedJobId);
		return !!meta?.finalized;
	};

	const finalizeLineResultTrack = (jobId: string) => {
		const normalizedJobId = String(jobId);
		finalizedLineResultJobs.add(normalizedJobId);
		const meta = getLineResultTrackMeta(jobId);
		lineResultTrackRef.current.set(normalizedJobId, {
			...meta,
			finalized: true
		});
	};

	const markLineResultRequested = (jobId: string) => {
		const normalizedJobId = String(jobId);
		requestedLineResultJobs.add(normalizedJobId);
		const meta = getLineResultTrackMeta(jobId);
		lineResultTrackRef.current.set(normalizedJobId, {
			...meta,
			resultRequested: true
		});
	};

	const isLineResultRequested = (jobId: string) => {
		const normalizedJobId = String(jobId);
		if (requestedLineResultJobs.has(normalizedJobId)) {
			return true;
		}
		const meta = lineResultTrackRef.current.get(normalizedJobId);
		return !!meta?.resultRequested;
	};

	const getLineResultExecuteInterval = (jobId: string) => {
		const meta = getLineResultTrackMeta(jobId);
		return Date.now() - meta.startTime;
	};

	const getPerilsValue = (layerIds: string[]) => {
		const hasFathom = layerIds.some((id) => id?.includes("Fathom"));
		const hasCatnet = layerIds.some((id) => id && !id.includes("Fathom"));

		if (hasFathom && hasCatnet) {
			return "fathom,catnet";
		}
		if (hasFathom) {
			return "fathom";
		}
		return "catnet";
	};

	const setLineAnalysisError = (jobId: string, traceInfo: string) => {
		reportLineResultPointFailureStatistics(jobId, traceInfo);
		dispatch({ type: AppAction.setLineAnalysisStatus, payload: "error" });
	};

	const isInvalidLineStatusResponse = (res: any) => {
		return !!(res && res.code !== 200 && !res.data?.[0]);
	};

	const getLineStatusItem = (res: any) => res?.data?.[0];

	const pollShapeAnalysisStatus = async ({
		jobId,
		isActive,
		fetchStatus,
		isRunning,
		onError,
		onCompleted,
		onRetry,
		onRetryOverflow
	}: {
		jobId: string;
		isActive: (id: string) => boolean;
		fetchStatus: (id: string) => Promise<any>;
		isRunning: () => boolean;
		onError: (id: string, message: string) => void;
		onCompleted: (id: string) => void;
		onRetry: (id: string) => void;
		onRetryOverflow: () => void;
	}) => {
		if (!isActive(jobId)) {
			return;
		}

		const res = await fetchStatus(jobId);
		if (!isActive(jobId)) {
			return;
		}

		if (isInvalidLineStatusResponse(res)) {
			if (isRunning()) {
				onError(jobId, String(res?.message));
			}
			return;
		}

		const status = getLineStatusItem(res);
		if (status?.hasError) {
			onError(jobId, String(status?.message));
			return;
		}

		if (status?.isCompleted) {
			requestNumberRef.current.line = 0;
			onCompleted(jobId);
			return;
		}

		setTimeout(() => {
			if (!isActive(jobId)) {
				return;
			}
			requestNumberRef.current.line++;
			if (requestNumberRef.current.line >= 11) {
				requestNumberRef.current.line = 0;
				onRetryOverflow();
				return;
			}
			onRetry(jobId);
		}, 5000);
	};

	const reportLineResultPointStatistics = (jobId: string, results: any[]) => {
		if (isLineResultFinalized(jobId)) {
			return;
		}

		const { shapeSource, layers } = getLineResultTrackMeta(jobId);

		if (!shapeSource) {
			return;
		}

		const eventType = `${shapeSource}-result-points`;
		const executeInterval = getLineResultExecuteInterval(jobId);
		const firstLayerId = results.find((item: any) => item?.layerId)?.layerId;
		if (!firstLayerId) {
			finalizeLineResultTrack(jobId);
			return;
		}
		const allLayerIds = Array.from(
			new Set(results.map((item: any) => item?.layerId).filter(Boolean))
		) as string[];

		const locationAmount = results.filter(
			(item: any) => item?.layerId === firstLayerId
		).length;

		layerStatistics({
			eventType,
			status: "1",
			perils: getPerilsValue(allLayerIds),
			layerAmount: allLayerIds.length || layers.length || 1,
			layers: allLayerIds.join(","),
			locationAmount,
			executeInterval
		});

		finalizeLineResultTrack(jobId);
	};

	const reportLineResultPointFailureStatistics = (jobId: string, traceInfo: string) => {
		if (isLineResultFinalized(jobId)) {
			return;
		}

		const { shapeSource, layers } = getLineResultTrackMeta(jobId);
		if (!shapeSource || !layers.length) {
			finalizeLineResultTrack(jobId);
			return;
		}

		const eventType = `${shapeSource}-result-points`;
		const executeInterval = getLineResultExecuteInterval(jobId);

		layerStatistics({
			eventType,
			status: "0",
			perils: getPerilsValue(layers),
			layerAmount: layers.length,
			layers: layers.join(","),
			locationAmount: 0,
			executeInterval,
			traceInfo
		});

		finalizeLineResultTrack(jobId);
	};

	const getLineAnalysisResultData = async (jobId: string) => {
		if (!activeLineJobRef.current.is(jobId)) {
			return;
		}
		if (isLineResultFinalized(jobId) || isLineResultRequested(jobId)) {
			return;
		}
		markLineResultRequested(jobId);

		let res: any = await getLineAnalysisResult(jobId);
		if (!activeLineJobRef.current.is(jobId)) {
			return;
		}
		if (res.code !== 200 || !res.data?.results?.length) {
			setLineAnalysisError(jobId, String(res?.message));
			return;
		}
		reportLineResultPointStatistics(jobId, res.data.results);
		dispatch({ type: AppAction.setLineAnalysisStatus, payload: undefined });
		dispatch({ type: AppAction.setLineAnalysisResult, payload: res.data.results });
	};

	const getLineAnalysisData = async () => {
		if (!context.lineJobId) {
			return;
		}
		let jobId = String(context.lineJobId);
		activeLineJobRef.current.activate(jobId);
		getLineResultTrackMeta(jobId);

		if (isLineResultFinalized(jobId)) {
			return;
		}

		let res: any = await getLineAnalysisStatus(jobId);
		if (!activeLineJobRef.current.is(jobId)) {
			return;
		}
		dispatch({ type: AppAction.setLineJobId, payload: undefined });
		if (isInvalidLineStatusResponse(res)) {
			if (context.lineAnalysisStatus === "running") {
				setLineAnalysisError(jobId, String(res?.message));
			}
			return;
		}

		const status = getLineStatusItem(res);
		if (status?.hasError) {
			setLineAnalysisError(jobId, String(status?.message));
			return;
		}

		if (status?.isCompleted) {
			getLineAnalysisResultData(jobId);
			return;
		}

		setTimeout(() => {
			if (!activeLineJobRef.current.is(jobId)) {
				return;
			}
			requestNumberRef.current.line = 0;
			resetLineAnalysisData(jobId);
		}, 3000);
	}

	const setPolygonAnalysisError = (jobId: string, message?: string) => {
		dispatch({ type: AppAction.setPolygonAnalysisStatus, payload: "error" });
		reportLineResultPointFailureStatistics(jobId, message || "");
	};

	const resetPolygonAnalysisData = async (jobId: string) => {
		await pollShapeAnalysisStatus({
			jobId,
			isActive: (id) => activePolygonJobRef.current.is(id),
			fetchStatus: getPolygonAnalysisStatus,
			isRunning: () => context.polygonAnalysisStatus === "running",
			onError: setPolygonAnalysisError,
			onCompleted: getPolygonAnalysisResultData,
			onRetry: resetPolygonAnalysisData,
			onRetryOverflow: () => {
				dispatch({ type: AppAction.setPolygonAnalysisStatus, payload: "error" });
			}
		});
	};

	const getPolygonAnalysisResultData = async (jobId: string) => {
		if (!activePolygonJobRef.current.is(jobId)) {
			return;
		}
		if (isLineResultFinalized(jobId) || isLineResultRequested(jobId)) {
			return;
		}
		markLineResultRequested(jobId);

		let res: any = await getPolygonAnalysisResult(jobId);
		if (!activePolygonJobRef.current.is(jobId)) {
			return;
		}
		if (res.code !== 200 || !res.data?.results?.length) {
			setPolygonAnalysisError(jobId, String(res?.message));
			return;
		}
		const responseSpatial = String(res.data?.spatial || "").toUpperCase() === "WGS84" ? "WGS84" : "GCJ02";
		const polygonResultsWithSpatial = (res.data.results || []).map((item: any) => ({
			...item,
			spatial: String(item?.spatial || "").toUpperCase() === "WGS84" ? "WGS84" : responseSpatial
		}));

		reportLineResultPointStatistics(jobId, polygonResultsWithSpatial);
		dispatch({ type: AppAction.setPolygonAnalysisStatus, payload: undefined });
		dispatch({ type: AppAction.setPolygonAnalysisResult, payload: polygonResultsWithSpatial });
		const polygonH3Resolution = Number(res.data?.statisticResults?.summary?.h3Resolution);
		dispatch({
			type: AppAction.setPolygonH3Resolution,
			payload: Number.isFinite(polygonH3Resolution) ? polygonH3Resolution : undefined
		});
		dispatch({
			type: AppAction.setPolygonLayerStatistics,
			payload: res.data?.statisticResults?.layerStatistics
		});
	};

	const getPolygonAnalysisData = async () => {
		if (!context.polygonJobId) {
			return;
		}
		let jobId = String(context.polygonJobId);
		activePolygonJobRef.current.activate(jobId);
		getLineResultTrackMeta(jobId);

		if (isLineResultFinalized(jobId)) {
			return;
		}

		let res: any = await getPolygonAnalysisStatus(jobId);
		if (!activePolygonJobRef.current.is(jobId)) {
			return;
		}
		dispatch({ type: AppAction.setPolygonJobId, payload: undefined });
		if (isInvalidLineStatusResponse(res)) {
			if (context.polygonAnalysisStatus === "running") {
				setPolygonAnalysisError(jobId, String(res?.message));
			}
			return;
		}

		const status = getLineStatusItem(res);
		if (status?.hasError) {
			setPolygonAnalysisError(jobId, String(status?.message));
			return;
		}

		if (status?.isCompleted) {
			getPolygonAnalysisResultData(jobId);
			return;
		}

		setTimeout(() => {
			if (!activePolygonJobRef.current.is(jobId)) {
				return;
			}
			requestNumberRef.current.line = 0;
			resetPolygonAnalysisData(jobId);
		}, 3000);
	}

	useEffect(() => {
		if (context.rightPanel === RightPanel.Single && context.singleLocationAnalysed) {
			if (!context.analysisData) {
				setOption(Option.CLIMATE);
				dispatch({ type: AppAction.setOption, payload: Option.CLIMATE });
			} else if (!(context.batchId || context.futureHazardBatchId || context.crsComponentBatchId)) {
				setOption(Option.HAZARD);
				dispatch({ type: AppAction.setOption, payload: Option.HAZARD });
			}
		}
	}, [context.analysisData, context.batchId, context.futureHazardBatchId, context.crsComponentBatchId]);

	useEffect(() => {
		if ((context.rightPanel === RightPanel.Multiple) && locationSetAnalysed && context.jobId) {
			getMultipleAnalysisData();
		}
	}, [context.jobId]);

	useEffect(() => {
		if (context.rightPanel === RightPanel.Line && context.lineJobId) {
			getLineAnalysisData();
		} else if (context.rightPanel === RightPanel.Polygon && context.polygonJobId) {
			getPolygonAnalysisData();
		}
	}, [context.lineJobId, context.polygonJobId, context.rightPanel]);

	useEffect(() => {
		if ((context.rightPanel === RightPanel.Multiple) && locationSetAnalysed) {
			if (context.hazardMultipleAnalysisStatus === "running") {
				setOption(Option.HAZARD);
				dispatch({ type: AppAction.setOption, payload: Option.HAZARD });
			} else if (context.climateMultipleAnalysisStatus === "running") {
				setOption(Option.CLIMATE);
				dispatch({ type: AppAction.setOption, payload: Option.CLIMATE });
			} else if (context.eventAnalysisStatus === "running") {
				setOption(Option.EVENT);
				dispatch({ type: AppAction.setOption, payload: Option.EVENT });
			}
		}
	}, [context.hazardMultipleAnalysedLegends, context.climateMultipleAnalysedLegends, context.eventAnalysisLegend]);

	return (
		<div>
			{context.rightPanel === RightPanel.Single && context.singleLocationAnalysed && (
				<>
					<div className="m-3">
						<Options
							hazardLayerLength={!context.analysisData || typeof context.analysisData?.legendItems === "string" ? 0 : context.analysisData?.legendItems?.length}
							selected={option}
							onChange={selectOption}
						/>
					</div>
					{option === Option.HAZARD && (
						<>
							<div className={styles.singleHazardInfo}>
								<div
									className="tooltip tooltip-left z-50 w-[35px] h-[24px]"
									data-tip={t("results.singleIntensityTip")}
								></div>
							</div>
							<div className={styles.resultsSingle}>
								<AnalysisResults />
							</div>
						</>
					)}
					{option === Option.CLIMATE && (
						<div className={styles.resultsSingle}>
							<ClimateResults />
						</div>
					)}
				</>
			)}

			{context.rightPanel === RightPanel.Multiple && locationSetAnalysed && (
				<>
					<div className="m-3">
						<Options
							hazardLayerLength={context.hazardMultipleAnalysedLegends ? context.hazardMultipleAnalysedLegends.length : 0}
							climateLayerLength={context.climateMultipleAnalysedLegends ? context.climateMultipleAnalysedLegends.length : 0}
							eventLength={context.selectedEvent ? 1 : 0}
							selected={option}
							onChange={selectOption}
						/>
					</div>
					<div className={styles.resultsMultiple}>
						{option === Option.HAZARD && <HazardMultipleResults />}
						{option === Option.CLIMATE && <ClimateMultipleResults />}
						{option === Option.EVENT && <EventMultipleResults />}
					</div>
				</>
			)}
			{context.rightPanel === RightPanel.Line && <div className="m-3 pt-3"><LineResults /></div>}
			{(context.rightPanel === RightPanel.Polygon || !!context.polygonExpandedLayerId) && (
				<div
					className={context.rightPanel === RightPanel.Polygon ? "m-3 pt-3" : undefined}
					style={context.rightPanel === RightPanel.Polygon ? undefined : { display: "none" }}
					aria-hidden={context.rightPanel === RightPanel.Polygon ? undefined : true}
				>
					<PolygonResults />
				</div>
			)}
		</div>
	);
}

export default Results;

